import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Checkbox,
  Modal,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  TextArea,
  TextInput,
} from '@carbon/react';
import axios from 'axios';
import moment from 'moment';

import { GatewayIp } from '../../../data/endpoints';
import { IScheduleRetryPolicy, ISchedulerProfile, ISchedulerSlot } from '../../../AppSlice';
import RecorderAdvancedConfigForm, {
  AdvancedConfig,
  AdvancedScheduleConfig,
  FilesystemResource,
  RtmpResource,
  SdiResource,
  buildAdvancedConfigParams,
  emptyAdvancedConfig,
  emptyAdvancedScheduleConfig,
  parseAdvancedConfig,
} from './RecorderAdvancedConfigForm';

// ─── Types ───────────────────────────────────────────────────────────────────

interface SlotFormData {
  label: string;
  enabled: boolean;
  description: string;
}

interface ISlotDefaultConfig {
  id: string;
  taskType: string;
  params: Record<string, any>;
  enabled?: boolean;
  tz?: string;
  overlapPolicy?: string;
  misfirePolicy?: string;
  retryPolicy?: IScheduleRetryPolicy;
  validFrom: string;
  validTo: string | null;
  createdAt: string;
  updatedAt: string;
}

interface Props {
  open: boolean;
  slot: ISchedulerSlot | null;
  token: string | null;
  mediaengineProfiles?: ISchedulerProfile[];
  sdiResources?: SdiResource[];
  filesystemResources?: FilesystemResource[];
  rtmpResources?: RtmpResource[];
  onClose: () => void;
  onSave: (data: SlotFormData) => void;
  onDelete: (slotId: string) => void;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const slotBase = (slotId: string) => `${GatewayIp}/api/scheduler/slots/${slotId}`;

const authHeaders = (token: string) => ({
  Authorization: `Bearer ${token}`,
  'Content-Type': 'application/json',
});

const btnStyle = (color: string, danger = false): React.CSSProperties => ({
  backgroundColor: danger ? 'transparent' : color,
  color: danger ? color : '#fff',
  border: `1px solid ${color}`,
  padding: '8px 20px',
  cursor: 'pointer',
  fontSize: 14,
  borderRadius: 4,
});

// ─── Component ───────────────────────────────────────────────────────────────

const SlotFormModal: React.FC<Props> = ({
  open,
  slot,
  token,
  mediaengineProfiles = [],
  sdiResources = [],
  filesystemResources = [],
  rtmpResources = [],
  onClose,
  onSave,
  onDelete,
}) => {
  const isEditMode = Boolean(slot);

  // ── shared ──
  const [activeTabIndex, setActiveTabIndex] = useState(0);

  // ── settings tab ──
  const [label, setLabel] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [description, setDescription] = useState('');
  const [labelError, setLabelError] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);

  // ── default-configs tab ──
  const [defaultConfigs, setDefaultConfigs] = useState<ISlotDefaultConfig[]>([]);
  const [loadingConfigs, setLoadingConfigs] = useState(false);
  const [configsError, setConfigsError] = useState('');
  const [showNewConfigForm, setShowNewConfigForm] = useState(false);
  const [submittingConfig, setSubmittingConfig] = useState(false);
  const [closingConfigId, setClosingConfigId] = useState<string | null>(null);
  const [activatingConfigId, setActivatingConfigId] = useState<string | null>(null);

  // ── new-config form state ──
  const [newConfig, setNewConfig] = useState<AdvancedConfig>(emptyAdvancedConfig);
  const [newScheduleConfig, setNewScheduleConfig] = useState<AdvancedScheduleConfig>(emptyAdvancedScheduleConfig);

  const profileOptions = useMemo(() =>
    (mediaengineProfiles ?? []).filter((p) => p?.id && p?.name),
    [mediaengineProfiles]
  );

  useEffect(() => {
    if (!open) return;
    setActiveTabIndex(0);
    setLabel(slot?.label ?? '');
    setEnabled(slot?.enabled ?? true);
    setDescription(slot?.description ?? '');
    setLabelError('');
    setConfirmDelete(false);
    setDefaultConfigs([]);
    setConfigsError('');
    setShowNewConfigForm(false);
    setSubmittingConfig(false);
    setClosingConfigId(null);
    setActivatingConfigId(null);
    setNewConfig(emptyAdvancedConfig());
    setNewScheduleConfig(emptyAdvancedScheduleConfig());
  }, [open, slot]);

  const activeConfig = defaultConfigs.find((c) => c.validTo === null) ?? null;

  const loadConfigs = useCallback(async () => {
    if (!slot || !token) return;
    setLoadingConfigs(true);
    setConfigsError('');
    try {
      const res = await axios.get(`${slotBase(slot.id)}/default-configs`, {
        withCredentials: true,
        headers: authHeaders(token),
      });
      const data = res.data;
      setDefaultConfigs(Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : []);
    } catch (err: any) {
      setConfigsError(err?.response?.data?.message ?? 'Failed to load configs');
    } finally {
      setLoadingConfigs(false);
    }
  }, [slot, token]);

  useEffect(() => {
    if (open && activeTabIndex === 1) loadConfigs();
  }, [open, activeTabIndex, loadConfigs]);

  // ── settings ──
  const handleSaveSettings = () => {
    if (!label.trim()) { setLabelError('Label is required'); return; }
    onSave({ label: label.trim(), enabled, description: description.trim() });
  };

  // ── default-configs ──
  const openNewConfigForm = () => {
    const currentActive = defaultConfigs.find((c) => c.validTo === null) ?? null;
    setNewConfig(
      currentActive
        ? parseAdvancedConfig(currentActive.taskType, currentActive.params)
        : emptyAdvancedConfig()
    );
    setNewScheduleConfig(
      currentActive
        ? {
            enabled: currentActive.enabled ?? true,
            tz: currentActive.tz ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
            overlapPolicy: currentActive.overlapPolicy ?? 'SKIP_IF_RUNNING',
            misfirePolicy: currentActive.misfirePolicy ?? 'START_IF_BEFORE_END',
            retryMaxAttempts: Number(currentActive.retryPolicy?.maxAttempts ?? 3),
            retryInitialMs: Number(currentActive.retryPolicy?.initialMs ?? 1000),
            retryBackoff: currentActive.retryPolicy?.backoff ?? 'exponential',
          }
        : emptyAdvancedScheduleConfig()
    );
    setShowNewConfigForm(true);
  };

  const handleCreateConfig = async () => {
    if (!slot || !token) return;
    setSubmittingConfig(true);
    setConfigsError('');
    try {
      const payload = {
        taskType: newConfig.taskType.trim(),
        enabled: false, // newScheduleConfig.enabled,
        // tz: newScheduleConfig.tz,
        // overlapPolicy: newScheduleConfig.overlapPolicy,
        // misfirePolicy: newScheduleConfig.misfirePolicy,
        // retryPolicy: {
        //   maxAttempts: Number(newScheduleConfig.retryMaxAttempts),
        //   initialMs: Number(newScheduleConfig.retryInitialMs),
        //   backoff: newScheduleConfig.retryBackoff,
        // },
        params: buildAdvancedConfigParams(newConfig),
      };
      await axios.post(
        `${slotBase(slot.id)}/default-configs`,
        payload,
        { withCredentials: true, headers: authHeaders(token) }
      );
      setShowNewConfigForm(false);
      setNewConfig(emptyAdvancedConfig());
      setNewScheduleConfig(emptyAdvancedScheduleConfig());
      await loadConfigs();
    } catch (err: any) {
      setConfigsError(err?.response?.data?.message ?? 'Failed to create config');
    } finally {
      setSubmittingConfig(false);
    }
  };

  const handleCloseConfig = async (configId: string) => {
    if (!slot || !token) return;
    setClosingConfigId(configId);
    setConfigsError('');
    try {
      await axios.post(
        `${slotBase(slot.id)}/default-configs/${configId}/close`,
        {},
        { withCredentials: true, headers: authHeaders(token) }
      );
      await loadConfigs();
    } catch (err: any) {
      setConfigsError(err?.response?.data?.message ?? 'Failed to close config');
    } finally {
      setClosingConfigId(null);
    }
  };

  const handleActivateConfig = async (cfg: ISlotDefaultConfig) => {
    if (!slot || !token) return;
    setActivatingConfigId(cfg.id);
    setConfigsError('');
    try {
      await axios.post(
        `${slotBase(slot.id)}/default-configs`,
        { taskType: cfg.taskType, params: cfg.params },
        { withCredentials: true, headers: authHeaders(token) }
      );
      await loadConfigs();
    } catch (err: any) {
      setConfigsError(err?.response?.data?.message ?? 'Failed to activate config');
    } finally {
      setActivatingConfigId(null);
    }
  };

  // ── delete confirmation ──
  if (confirmDelete && slot) {
    return (
      <Modal
        open={open}
        modalHeading="Delete Slot"
        primaryButtonText="Delete"
        secondaryButtonText="Cancel"
        danger
        onRequestClose={() => setConfirmDelete(false)}
        onRequestSubmit={() => { onDelete(slot.id); setConfirmDelete(false); }}
        onSecondarySubmit={() => setConfirmDelete(false)}
        preventCloseOnClickOutside
      >
        <p>Are you sure you want to delete slot <strong>{slot.label}</strong>? This action cannot be undone.</p>
      </Modal>
    );
  }

  // ── create mode: simple form ──
  if (!isEditMode) {
    return (
      <Modal
        open={open}
        size="sm"
        modalHeading="Add Slot"
        primaryButtonText="Save"
        secondaryButtonText="Cancel"
        onRequestClose={onClose}
        onRequestSubmit={handleSaveSettings}
        onSecondarySubmit={onClose}
        preventCloseOnClickOutside
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <TextInput
            id="slot-label"
            labelText="Label"
            value={label}
            onChange={(e) => { setLabel(e.target.value); if (e.target.value.trim()) setLabelError(''); }}
            invalid={Boolean(labelError)}
            invalidText={labelError}
            placeholder="Enter slot label"
          />
          <Checkbox
            id="slot-enabled"
            labelText="Enabled"
            checked={enabled}
            onChange={(_, { checked }) => setEnabled(Boolean(checked))}
          />
          <TextArea
            id="slot-description"
            labelText="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Enter slot description"
            rows={3}
          />
        </div>
      </Modal>
    );
  }

  // ── edit mode ──
  return (
    <Modal
      open={open}
      size="lg"
      modalHeading={`Slot: ${slot?.label}`}
      passiveModal
      onRequestClose={onClose}
      preventCloseOnClickOutside
    >
      <Tabs
        selectedIndex={activeTabIndex}
        onChange={({ selectedIndex }) => setActiveTabIndex(selectedIndex)}
      >
        <TabList aria-label="Slot management" contained>
          <Tab>Settings</Tab>
          <Tab>Default Config</Tab>
        </TabList>

        <TabPanels>
          {/* ── Settings ── */}
          <TabPanel>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16, paddingTop: 16 }}>
              <TextInput
                id="slot-label"
                labelText="Label"
                value={label}
                onChange={(e) => { setLabel(e.target.value); if (e.target.value.trim()) setLabelError(''); }}
                invalid={Boolean(labelError)}
                invalidText={labelError}
                placeholder="Enter slot label"
              />
              <Checkbox
                id="slot-enabled"
                labelText="Enabled"
                checked={enabled}
                onChange={(_, { checked }) => setEnabled(Boolean(checked))}
              />
              <TextArea
                id="slot-description"
                labelText="Description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter slot description"
                rows={3}
              />
              <div style={{ display: 'flex', gap: 12, paddingTop: 8 }}>
                <button type="button" onClick={handleSaveSettings} style={btnStyle('#0f62fe')}>Save</button>
                <button type="button" onClick={() => setConfirmDelete(true)} style={btnStyle('#da1e28', true)}>Delete Slot</button>
              </div>
            </div>
          </TabPanel>

          {/* ── Default Config ── */}
          <TabPanel>
            <div style={{ paddingTop: 16 }}>
              {loadingConfigs ? (
                <div style={{ color: '#8d8d8d' }}>Loading configs…</div>
              ) : (
                <>
                  {configsError && (
                    <div style={{ color: '#da1e28', marginBottom: 12, padding: '8px 12px', backgroundColor: '#2d1515', borderRadius: 4 }}>
                      {configsError}
                    </div>
                  )}

                  {/* Active Config */}
                  <section style={{ marginBottom: 24 }}>
                    <h5 style={{ marginBottom: 10, fontSize: 14, color: '#ccc' }}>Active Config</h5>
                    {activeConfig ? (
                      <div style={{ backgroundColor: '#1e1e1e', border: '1px solid #24a148', borderRadius: 6, padding: 16 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ marginBottom: 6 }}>
                              <span style={{ color: '#8d8d8d', fontSize: 12 }}>Task Type: </span>
                              <code style={{ color: '#78a9ff' }}>{activeConfig.taskType}</code>
                            </div>
                            <pre style={{ margin: '4px 0 0', fontSize: 12, color: '#ccc', overflow: 'auto', maxHeight: 120, backgroundColor: '#111', padding: 8, borderRadius: 4 }}>
                              {JSON.stringify(activeConfig.params, null, 2)}
                            </pre>
                            <div style={{ marginTop: 8, fontSize: 11, color: '#6f6f6f' }}>
                              Valid from: {moment(activeConfig.validFrom).format('DD.MM.YYYY HH:mm')}
                            </div>
                          </div>
                          <button
                            type="button"
                            disabled={closingConfigId === activeConfig.id}
                            onClick={() => handleCloseConfig(activeConfig.id)}
                            style={{ ...btnStyle('#da1e28', true), fontSize: 12, padding: '6px 14px', whiteSpace: 'nowrap', opacity: closingConfigId === activeConfig.id ? 0.6 : 1 }}
                          >
                            {closingConfigId === activeConfig.id ? 'Closing…' : 'Close Config'}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div style={{ color: '#8d8d8d', fontSize: 13 }}>No active config.</div>
                    )}
                  </section>

                  {/* New Config Form */}
                  <section style={{ marginBottom: 24 }}>
                    {!showNewConfigForm ? (
                      <button type="button" onClick={openNewConfigForm} style={btnStyle('#6929c4')}>
                        + Set New Default Config
                      </button>
                    ) : (
                      <div style={{ backgroundColor: '#1e1e1e', border: '1px solid #525252', borderRadius: 6, padding: 16 }}>
                        <h5 style={{ marginBottom: 16, fontSize: 14, color: '#ccc' }}>New Default Config</h5>

                        <RecorderAdvancedConfigForm
                          value={newConfig}
                          onChange={(updates) => setNewConfig((prev) => ({ ...prev, ...updates }))}
                          sdiResources={sdiResources}
                          filesystemResources={filesystemResources}
                          rtmpResources={rtmpResources}
                          mediaengineProfiles={profileOptions}
                          showFilename={true}
                          scheduleConfig={newScheduleConfig}
                          onScheduleConfigChange={(updates) => setNewScheduleConfig((prev) => ({ ...prev, ...updates }))}
                          idPrefix="dc"
                        />

                        <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
                          <button
                            type="button"
                            onClick={handleCreateConfig}
                            disabled={submittingConfig}
                            style={{ ...btnStyle('#0f62fe'), opacity: submittingConfig ? 0.6 : 1 }}
                          >
                            {submittingConfig ? 'Creating…' : 'Create'}
                          </button>
                          <button
                            type="button"
                            onClick={() => { setShowNewConfigForm(false); setNewConfig(emptyAdvancedConfig()); }}
                            style={btnStyle('#525252', true)}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </section>

                  {/* History */}
                  {defaultConfigs.length > 0 && (
                    <section>
                      <h5 style={{ marginBottom: 10, fontSize: 14, color: '#ccc' }}>History</h5>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {[...defaultConfigs]
                          .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
                          .map((cfg) => {
                            const isActive = cfg.validTo === null;
                            const isActivating = activatingConfigId === cfg.id;
                            const isClosing = closingConfigId === cfg.id;

                            return (
                              <div
                                key={cfg.id}
                                style={{
                                  display: 'flex',
                                  alignItems: 'flex-start',
                                  gap: 12,
                                  padding: '10px 14px',
                                  backgroundColor: '#161616',
                                  borderRadius: 4,
                                  border: `1px solid ${isActive ? '#24a148' : '#393939'}`,
                                  opacity: isActive ? 1 : 0.75,
                                }}
                              >
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                                    <code style={{ fontSize: 13, color: '#78a9ff' }}>{cfg.taskType}</code>
                                    {isActive ? (
                                      <span style={{ fontSize: 10, backgroundColor: '#24a148', color: '#fff', padding: '1px 6px', borderRadius: 10 }}>Active</span>
                                    ) : (
                                      <span style={{ fontSize: 10, backgroundColor: '#393939', color: '#8d8d8d', padding: '1px 6px', borderRadius: 10 }}>Inactive</span>
                                    )}
                                  </div>
                                  <div style={{ fontSize: 11, color: '#6f6f6f' }}>
                                    Valid from: {moment(cfg.validFrom).format('DD.MM.YYYY HH:mm')}
                                    {cfg.validTo && <> · Until: {moment(cfg.validTo).format('DD.MM.YYYY HH:mm')}</>}
                                  </div>
                                </div>
                                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                                  {isActive && (
                                    <button
                                      type="button"
                                      onClick={() => handleCloseConfig(cfg.id)}
                                      disabled={isClosing}
                                      style={{ ...btnStyle('#da1e28', true), fontSize: 12, padding: '4px 12px', opacity: isClosing ? 0.6 : 1 }}
                                    >
                                      {isClosing ? '…' : 'Close'}
                                    </button>
                                  )}
                                  {!isActive && (
                                    <button
                                      type="button"
                                      onClick={() => handleActivateConfig(cfg)}
                                      disabled={isActivating}
                                      style={{ ...btnStyle('#24a148', true), fontSize: 12, padding: '4px 12px', opacity: isActivating ? 0.6 : 1 }}
                                    >
                                      {isActivating ? '…' : 'Activate'}
                                    </button>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    </section>
                  )}
                </>
              )}
            </div>
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Modal>
  );
};

export default SlotFormModal;
